import React, { useCallback, useEffect, useState } from 'react';
import { HolderOutlined } from '@ant-design/icons';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useSelector, useDispatch } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { PartialData, DataType } from '@kemu-io/hs-types';
import statusManager from '@kemu-io/kemu-core/common/managers/statusManager.js';
import styles from './StatusPanel.module.css';
import { selectLogicMapperSettings, setLogicMapperSettingValue } from '@src/features/interface/interfaceSlice';
import { selectCurrentRecipePoolId, selectOpeningRecipe } from '@src/features/Workspace/workspaceSlice';
import { useTranslation } from '@hooks/index';
import { useMinimizedPanels } from '@components/MinimizedPanelsContainer/MinimizedPanelsContext';
import DataTypeIcon from '@components/DataTypeIcon/DataTypeIcon';
import { Popover, Tooltip } from 'antd';
import useNavigateToWidgetPath from '@hooks/useNavigateToWidgetPath';
import { useDebouncedCallback } from 'use-debounce';
import { WidgetType } from '@kemu-io/kemu-core/types';
import usePanelMinimize from '@src/hooks/usePanelMinimize';
import PanelMinimizeButton from '@components/PanelMinimizeButton/PanelMinimizeButton';

type Props = {
  containerEl: HTMLDivElement | null;
}

const minPanelSize = { width: 300, height: 150 };
const STATUS_PANEL_WIDTH = minPanelSize.width;
const STATUS_PANEL_HEIGHT = minPanelSize.height;
const STATUS_PANEL_MARGIN = 10;

type StatusEntry = {
  key: string;
  name: string;
  value: PartialData;
  statusWidgetId: string;
  widgetType: WidgetType;
}

const StatusPanel = (props: Props) => {
  const { containerEl } = props;
  const userSettings = useSelector(selectLogicMapperSettings);
  const defaultPanelPosition = userSettings.statusPanelPosition;
  const defaultPanelSize = userSettings.statusPanelSize || minPanelSize;
  const [panelPosition, setPanelPosition] = useState(defaultPanelPosition);
  const [panelSize, setPanelSize] = useState<{ width: number, height: number }>(defaultPanelSize);
  const [statusEntries, setStatusEntries] = useState<StatusEntry[]>([]);
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const dispatch = useDispatch();
  const t = useTranslation('StatusPanel');
  const dataTypeT = useTranslation('Kemu.DataType.HumanReadable');
  const { registerExpandHandler, unregisterPanel } = useMinimizedPanels();
  const recipePoolId = useSelector(selectCurrentRecipePoolId);
  const openingRecipe = useSelector(selectOpeningRecipe);
  const { ref, width: screenWidth, height: screenHeight } = useResizeDetector();
  const navigateToWidgetPath = useNavigateToWidgetPath();
  const [showPopConfirmForEntry, setShowPopConfirmForEntry] = useState<StatusEntry | null>(null);

  const { isMinimized, handleMinimize, handleExpand } = usePanelMinimize({
    panelId: 'status',
    currentPosition: panelPosition,
    currentSize: panelSize,
    setPanelPosition,
    setPanelSize,
  });

  const canShowPanel = recipePoolId && !openingRecipe;

  /**
   * Handles clicking on a widget name to navigate to the widget
   * @param widgetId - The ID of the widget to navigate to
  */
 const handleWidgetNameClick = (widgetId: string) => {
   navigateToWidgetPath({ widgetId });
  };

  const debouncedClick = useDebouncedCallback((widgetId: string) => {
    handleWidgetNameClick(widgetId);
  }, 200);

  /**
   * Handles double-clicking on a widget name to start editing
   * @param entry - The status entry being edited
   */
  const handleNameDoubleClick = (entry: StatusEntry) => {
    debouncedClick.cancel();

    if(entry.widgetType === WidgetType.script) {
      setShowPopConfirmForEntry(entry);
      return;
    }

    setEditingEntry(entry.key);
    setEditingValue(entry.name);
  };

  /**
   * Handles input change while editing
   * @param value - The new value
   */
  const handleInputChange = (value: string) => {
    setEditingValue(value);
  };

  /**
   * Handles saving the edited name
   * @param entry - The status entry being edited
   */
  const handleSaveEdit = (entry: StatusEntry) => {
    const newName = editingValue.trim();
    if (newName && newName !== entry.name) {
      statusManager.updateStatusName({
        newName,
        statusWidgetId: entry.statusWidgetId,
      });
    }
    setEditingEntry(null);
    setEditingValue('');
  };

  const handleClearStatusValue = (entry: StatusEntry) => {
    statusManager.setStatusValue({
      name: entry.name,
      widgetId: entry.statusWidgetId,
      value: null,
      widgetType: entry.widgetType,
      sourceWidget: ''
    });
  }

  /**
   * Handles canceling the edit
   */
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditingValue('');
  };

  /**
   * Handles key press events in the input
   * @param event - The keyboard event
   * @param entry - The status entry being edited
   */
  const handleKeyPress = (event: React.KeyboardEvent, entry: StatusEntry) => {
    if (event.key === 'Enter') {
      handleSaveEdit(entry);
    } else if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  /**
   * Gets the human-readable name for a data type
   * @param dataType - The DataType enum value
   * @returns The translated data type name
   */
  const getDataTypeName = (dataType: DataType): string => {
    switch (dataType) {
      case DataType.Number:
        return dataTypeT('Number');
      case DataType.String:
        return dataTypeT('String');
      case DataType.Array:
        return dataTypeT('Array');
      case DataType.JsonObj:
        return dataTypeT('JsonObj');
      case DataType.ImageData:
        return dataTypeT('ImageData');
      case DataType.Boolean:
        return dataTypeT('Boolean');
      case DataType.Rect:
        return dataTypeT('Rect');
      case DataType.BinaryFile:
        return dataTypeT('BinaryFile');
      case DataType.ArrayBuffer:
        return dataTypeT('ArrayBuffer');
      case DataType.AudioBuffer:
        return dataTypeT('AudioBuffer');
      case DataType.Anything:
        return dataTypeT('Anything');
      default:
        return dataTypeT('Anything');
    }
  };

  /**
   * Updates the status entries from the status manager
   */
  const updateStatusEntries = useCallback(() => {
    const allStatusValues = statusManager.getAllStatusValues();
    const entries: StatusEntry[] = [];
    for(const widgetId in allStatusValues){
      const statuses = allStatusValues[widgetId];
      for(const status of statuses){
        entries.push({
          key: `${widgetId}_${status.name}`,
          name: status.name,
          value: status.data || { type: DataType.Anything, value: '' },
          statusWidgetId: widgetId,
          widgetType: status.widgetType,
        });
      }
    }
    // Object.values(allStatusValues).map((statuses) => {
    //   return 
    //   name: statuses.name,
    //   value: value.data || { type: DataType.Anything, value: '' },
    //   statusWidgetId: value.statusWidgetId,
    // }));

    // Sort by widget Id
    entries.sort((a, b) => a.statusWidgetId.localeCompare(b.statusWidgetId));
    setStatusEntries(entries);
  }, []);

  const handleResize = useCallback((event: React.SyntheticEvent, data: ResizeCallbackData) => {
    let newWidth = Math.max(minPanelSize.width, data.size.width);
    let newHeight = Math.max(minPanelSize.height, data.size.height);
    const rightEdge = panelPosition.x + newWidth + STATUS_PANEL_MARGIN;
    const bottomEdge = panelPosition.y + newHeight + STATUS_PANEL_MARGIN;

    if(screenWidth && rightEdge > screenWidth) {
      newWidth = screenWidth - panelPosition.x - STATUS_PANEL_MARGIN;
    }

    if(screenHeight && bottomEdge > screenHeight) {
      newHeight = screenHeight - panelPosition.y - STATUS_PANEL_MARGIN;
    }

    const finalSize = {
      width: newWidth,
      height: newHeight,
    };

    setPanelSize(finalSize);

    dispatch(setLogicMapperSettingValue({
      key: 'statusPanelSize',
      value: finalSize,
    }));
  }, [screenWidth, screenHeight, panelPosition, dispatch]);

  const handleStopDragging = (_: DraggableEvent, data: DraggableData) => {
    setPanelPosition({
      x: data.x,
      y: data.y,
    });

    dispatch(setLogicMapperSettingValue({
      key: 'statusPanelPosition',
      value: {
        x: data.x,
        y: data.y,
      },
    }));
  };


  useEffect(() => {
    if (containerEl) {
      ref.current = containerEl;
    }
  }, [containerEl, ref]);

  // Prevents panel from being outside the visible workspace
  useEffect(() => {
    if (screenWidth && screenHeight) {
      setPanelPosition((current) => {
        const panelX = screenWidth && screenWidth < (current.x + STATUS_PANEL_WIDTH + STATUS_PANEL_MARGIN) ? screenWidth - STATUS_PANEL_WIDTH - STATUS_PANEL_MARGIN : current.x;
        const panelY = screenHeight && screenHeight < (current.y + STATUS_PANEL_HEIGHT + STATUS_PANEL_MARGIN) ? screenHeight - STATUS_PANEL_HEIGHT - STATUS_PANEL_MARGIN : current.y;
        return {
          x: panelX,
          y: panelY,
        };
      });
    }
  }, [screenWidth, screenHeight, panelSize]);

  // Initialize status entries and subscribe to changes
  useEffect(() => {
    updateStatusEntries();

    const unsubscribe = statusManager.onStatusValueChange(updateStatusEntries);
    const unsubscribeKeyRemoved = statusManager.onKeyRemoved(updateStatusEntries);
    return () => {
      unsubscribe();
      unsubscribeKeyRemoved();
    };
  }, [updateStatusEntries]);

  // Register/unregister expand handler with context
  useEffect(() => {
    if (isMinimized) {
      registerExpandHandler('status', handleExpand, t('Title'));
    } else {
      unregisterPanel('status');
    }
    return () => {
      unregisterPanel('status');
    };
  }, [isMinimized, handleExpand, t, registerExpandHandler, unregisterPanel]);

  if (!canShowPanel) { return null; }

  // If minimized, don't render the full panel (it will be rendered by MinimizedPanelsContainer)
  if (isMinimized) {
    return null;
  }

  const EntryField = ({ entry }: { entry: StatusEntry }) => (
    <div 
      className={styles.ClickableWidgetName}
      onClick={() => debouncedClick(entry.statusWidgetId)}
      onDoubleClick={() => handleNameDoubleClick(entry)}
      title={entry.widgetType === WidgetType.script ? undefined : t('FieldTitle', { name: entry.name })}
    >
      {entry.name}
    </div>
  )

  return (
    <Draggable
      onStop={handleStopDragging}
      bounds="parent"
      handle={`.${styles.Header}`}
      position={panelPosition}
      disabled={isMinimized}
    >
      <Resizable
        height={panelSize.height}
        width={panelSize.width}
        minConstraints={[minPanelSize.width, minPanelSize.height]}
        resizeHandles={['se']}
        onResize={handleResize}
      >
        <div
          className={styles.Container}
          style={{
            width: panelSize.width,
            height: panelSize.height,
          }}
        >
          <div className={styles.Header}>
            <span className={styles.DragIcon}>
              <HolderOutlined />
            </span>
            <span className={styles.Title}>{t('Title')}</span>
            <PanelMinimizeButton onClick={handleMinimize} tooltip={t('Minimize', 'Minimize')} />
          </div>
          
          <div className={styles.TableContainer}>
            <div className={styles.Table}>
              <div className={styles.TableHeader}>
                <div className={styles.TableHeaderCell}>{t('Name')}</div>
                <div className={styles.TableHeaderCell}>{t('Value')}</div>
              </div>
              <div className={styles.TableBody}>
                {statusEntries.map((entry) => (
                  <div key={entry.key} className={styles.TableRow}>
                    <div className={styles.TableCell}>
                      {editingEntry === entry.key ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => handleInputChange(e.target.value)}
                          onBlur={() => handleSaveEdit(entry)}
                          onKeyDown={(e) => handleKeyPress(e, entry)}
                          className={styles.EditableInput}
                          autoFocus
                        />
                      ) : (
                        <>
                          {showPopConfirmForEntry === entry ? (
                            <Popover
                              title={t('NotEditable.Title')}
                              content={t('NotEditable.Description')}
                              trigger="click"
                              defaultOpen={true}
                              onOpenChange={(open) => setShowPopConfirmForEntry(open ? entry : null)}
                              classNames={{
                                body: styles.PopoverBody,
                              }}
                            >
                              <EntryField entry={entry} />
                            </Popover>
                          ) : (
                            <EntryField entry={entry} />
                          )}
                        </>
                      )}
                    </div>
                    <div className={styles.TableCell}>
                      <div className={styles.ValueContainer}>
                        <Tooltip title={getDataTypeName(entry.value.type)} placement="top">
                          <div onDoubleClick={() => handleClearStatusValue(entry)}>
                            <DataTypeIcon dataType={entry.value.type} className={styles.DataTypeIcon} />
                          </div>
                        </Tooltip>
                        <pre className={styles.ValuePre}>
                          {entry.value.value.toString()}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Resizable>
    </Draggable>
  );
};

export default StatusPanel;
